import { supabase } from '../lib/supabase';
import { AppError, getErrorMessage } from '../utils/AppError';
import { logger } from '../utils/Logger';

interface ApiOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: 'POST' | 'GET' | 'PUT' | 'DELETE';
  timeout?: number;
  retries?: number;
}

export class ApiClient {
  private defaultTimeout = 30000; // 30 seconds
  private defaultRetries = 2;

  async get<T>(url: string, options: Omit<ApiOptions, 'method'> = {}) {
    const functionName = url.startsWith('/') ? url.slice(1) : url;
    return this.invoke<T>(functionName, { ...options, method: 'GET' });
  }

  async post<T>(url: string, body: unknown, options: Omit<ApiOptions, 'method' | 'body'> = {}) {
    const functionName = url.startsWith('/') ? url.slice(1) : url;
    return this.invoke<T>(functionName, { ...options, method: 'POST', body });
  }

  async put<T>(url: string, body: unknown, options: Omit<ApiOptions, 'method' | 'body'> = {}) {
    const functionName = url.startsWith('/') ? url.slice(1) : url;
    return this.invoke<T>(functionName, { ...options, method: 'PUT', body });
  }

  async delete<T>(url: string, options: Omit<ApiOptions, 'method'> = {}) {
    const functionName = url.startsWith('/') ? url.slice(1) : url;
    return this.invoke<T>(functionName, { ...options, method: 'DELETE' });
  }

  async invoke<T>(
    functionName: string,
    options: ApiOptions = {}
  ): Promise<{ data: T | null; error: AppError | null }> {
    const {
      body,
      headers = {},
      method = 'POST',
      timeout = this.defaultTimeout,
      retries = this.defaultRetries,
    } = options;

    let attempt = 0;

    while (attempt <= retries) {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          throw new AppError('Not authenticated', 'AUTH_ERROR', 401);
        }

        // Create a timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new AppError(`Request timed out after ${timeout}ms`, 'TIMEOUT_ERROR', 408));
          }, timeout);
        });

        // Execute request with timeout race
        const requestPromise = supabase.functions.invoke<T>(functionName, {
          body: body as any,
          headers: {
            ...headers,
          },
          method,
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await Promise.race([requestPromise, timeoutPromise]) as any;

        if (error) {
          // Handle Supabase function errors
          const functionError = await this.extractFunctionError(error);
          logger.error(`API Error [${functionName}]`, { error, functionError });
          throw new AppError(
            functionError.message,
            functionError.code,
            functionError.status,
            true,
            { originalError: error, responseData: functionError.responseData }
          );
        }

        // Check if the response indicates an error (success: false pattern)
        // Edge functions return 200 with success: false for proper error message passthrough
        if (data && typeof data === 'object' && data.success === false) {
          const errorMessage = getErrorMessage(
            data.error ?? data.errorDetails,
            'Operation failed',
          );
          logger.error(`API Error [${functionName}] - Edge function returned error`, { error: errorMessage, data });
          throw new AppError(
            errorMessage,
            data.errorDetails?.code || data.error?.code || 'EDGE_FUNCTION_ERROR',
            data.errorDetails?.originalStatus || 400,
            true,
            { responseData: data }
          );
        }

        return { data: data as T, error: null };

      } catch (err: unknown) {
        attempt++;
        const isLastAttempt = attempt > retries;
        const isRetryable = this.isRetryableError(err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';

        logger.warn(`Attempt ${attempt} failed for [${functionName}]`, { error: errorMessage });

        if (isLastAttempt || !isRetryable) {
          const appError = err instanceof AppError
            ? err
            : new AppError(errorMessage, 'UNKNOWN_ERROR', 500, true, { originalError: err });

          logger.error(`Final failure for [${functionName}]`, appError);
          return { data: null, error: appError };
        }

        // Wait before retrying (exponential backoff)
        await this.delay(Math.pow(2, attempt) * 1000);
      }
    }

    return { data: null, error: new AppError('Max retries exceeded', 'MAX_RETRIES', 500) };
  }

  private isRetryableError(error: unknown): boolean {
    // Retry on network errors or 5xx server errors
    // Don't retry on 4xx client errors (except 408/429)
    if (error instanceof AppError) {
      return [408, 429, 500, 502, 503, 504].includes(error.status);
    }
    return true; // Assume unknown errors might be transient network issues
  }

  private async extractFunctionError(error: any): Promise<{
    message: string;
    code: string;
    status: number;
    responseData?: unknown;
  }> {
    const fallback = {
      message: getErrorMessage(error, 'API invocation failed'),
      code: 'API_ERROR',
      status: typeof error?.context?.status === 'number' ? error.context.status : 500,
      responseData: undefined as unknown,
    };

    const response = error?.context;
    if (!response || typeof response.clone !== 'function') return fallback;

    try {
      const clone = response.clone();
      const contentType = clone.headers?.get?.('content-type') || '';
      const body = contentType.includes('application/json')
        ? await clone.json()
        : await clone.text();

      if (body && typeof body === 'object') {
        const message = getErrorMessage(body, fallback.message);
        return {
          message,
          code: body.errorDetails?.code || body.error?.code || body.code || fallback.code,
          status: body.errorDetails?.originalStatus || response.status || fallback.status,
          responseData: body,
        };
      }

      if (typeof body === 'string' && body.trim()) {
        return {
          ...fallback,
          message: body.trim(),
          status: response.status || fallback.status,
          responseData: body,
        };
      }
    } catch (parseError) {
      logger.warn('Failed to parse Supabase function error response', parseError);
    }

    return fallback;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const apiClient = new ApiClient();

